import { ACCOUNT_TYPE, selectUserData, updateValue } from 'containers/Account/accountSlice'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import { getPhoneParts, isValidPhone, PhoneValue } from 'utils/phone'
import { selectUser, updateUser } from 'containers/Login/loginSlice'
import { newsmscode, user as userUrl, verify } from 'rest/urls'
import useContactChangeLock from 'hooks/useContactChangeLock'
import { useReduxDispatch, useReduxSelector } from 'store'
import PhoneInput from 'components/inputs/PhoneInput'
import { UNAUTHORIZED_TYPES } from 'utils/constants'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toPhoneString } from 'utils/string'
import Popup from 'components/popups/Popup'
import PinForm from 'components/PinForm'
import { POST } from 'rest/request'

const CODE_LENGTH = 6
const RESEND_COOLDOWN = 60_000

type Step = 'info' | 'code' | 'edit'

interface PhoneResponse {
  phone_verified_at?: string | null
}

const UnverifiedPhonePopup: React.FC = () => {
  const user = useReduxSelector(selectUser)
  const accountData = useReduxSelector(state => selectUserData(state, ACCOUNT_TYPE.basic))

  const requiredFields = user?.hasRequiredInfo?.fields
  const phoneParts = getPhoneParts(requiredFields?.phone, requiredFields?.prefix)

  const [step, setStep] = useState<Step>('info')
  const [isLoading, setLoading] = useState(false)
  const [warning, setWarning] = useState('')
  const [code, setCode] = useState('')
  const [newPhone, setNewPhone] = useState<PhoneValue>(phoneParts)

  const sentDate = useRef<number>(0)

  const { hasRentals } = useContactChangeLock()
  const dispatch = useReduxDispatch()
  const { goBack } = useHistory()
  const { t } = useTranslation('chat')
  const { t: tForm } = useTranslation('form')

  const currentPhone = requiredFields?.phone ? toPhoneString(phoneParts.phone, phoneParts.prefix) : ''

  const markAsVerified = (verifiedAt?: string | null) => {
    dispatch(updateUser({ phone_verified_at: verifiedAt || new Date().toISOString() }))
    goBack()
  }

  const sendCode = async () => {
    if (isLoading) return
    if (sentDate.current && Date.now() - sentDate.current < RESEND_COOLDOWN) {
      dispatch(addNotification({ type: 'error', message: t('resendCooldown') }))
      return
    }

    setWarning('')
    setLoading(true)
    try {
      const response = await POST(newsmscode, { body: { prefix: phoneParts.prefix, phone: phoneParts.phone } })
      if (!response) throw new Error('Wrong response')
      sentDate.current = Date.now()
      setCode('')
      setStep('code')
    } catch (error) {
      console.error(error)
      setWarning(t('sendCodeFailed'))
    } finally {
      setLoading(false)
    }
  }

  const verifyCode = async () => {
    if (isLoading || code.length !== CODE_LENGTH) return
    setWarning('')
    setLoading(true)
    try {
      const body = { prefix: phoneParts.prefix, phone: phoneParts.phone, token: code }
      const response = await POST(verify(UNAUTHORIZED_TYPES.phone), { body })
      if (!response || response.verified === false) throw new Error('Wrong code')
      markAsVerified(response.phone_verified_at)
    } catch {
      setWarning(t('incorrectCode'))
      setLoading(false)
    }
  }

  const savePhone = async () => {
    if (isLoading) return
    if (!isValidPhone(newPhone.prefix, newPhone.phone)) {
      setWarning(tForm(newPhone.phone ? 'wrongNumber' : 'enterPhoneNumber'))
      return
    }

    if (phoneParts.prefix === newPhone.prefix && phoneParts.phone === newPhone.phone) {
      setWarning('')
      setStep('info')
      return
    }

    setWarning('')
    setLoading(true)
    try {
      const body = { prefix: newPhone.prefix, phone: newPhone.phone }
      const response: PhoneResponse = await POST(`${userUrl}/phone`, { body })
      if (!response) throw new Error('Wrong response')

      dispatch(updateUser({
        hasRequiredInfo: {
          ...user?.hasRequiredInfo,
          fields: { ...requiredFields, ...body },
        },
      }))
      if (accountData?.data) {
        dispatch(updateValue({ type: ACCOUNT_TYPE.basic, name: 'data', value: { ...accountData.data, ...body } }))
      }
      markAsVerified(response.phone_verified_at)
    } catch (error) {
      console.error(error)
      setWarning(t('phoneChangeFailed'))
      setLoading(false)
    }
  }

  const goToStep = (next: Step) => {
    if (isLoading) return
    setWarning('')
    if (next === 'edit') setNewPhone(phoneParts)
    setStep(next)
  }

  if (step === 'edit' && !hasRentals) return (
    <Popup>
      <div className='popup__verification'>
        <div className='popup__title'>
          <div>{tForm('changePhoneNumber')}</div>
        </div>

        <div className='popup__form'>
          <PhoneInput
            required
            autoFocus
            name='phone'
            value={newPhone.phone}
            prefix={newPhone.prefix}
            warning={warning}
            disabled={isLoading}
            label={tForm('phoneNumber')}
            placeholder={tForm('enterPhoneNumber')}
            enterClicked={savePhone}
            setValue={value => {
              setNewPhone(value)
              if (warning) setWarning('')
            }} />

          <div className='verification__buttons'>
            <ArrowBtn
              enlarged
              isLoading={isLoading}
              text={t('save')}
              action={savePhone} />
            <BackBtn
              text={t('cancel')}
              action={() => goToStep('info')} />
          </div>
        </div>
      </div>
    </Popup>
  )

  if (step === 'code') return (
    <Popup>
      <div className='popup__verification'>
        <div className='popup__title'>
          <div>{t('unverifiedPhoneTitle')}</div>
        </div>

        <div className='verification__text'>
          <div>{t('enterCodeSentTo')}:</div>
        </div>
        <h2>{currentPhone}</h2>

        <PinForm
          isCode
          autoFocus
          value={code}
          warning={!!warning}
          disabled={isLoading}
          setValue={setCode}
          setWarning={w => !w && setWarning('')}
          submit={verifyCode}
          autoLoadCodeFromSMS
        />
        {warning && <p className='verification__warning'>{warning}</p>}

        <div className='verification__button' onClick={sendCode}>
          {t('resendCode')}
        </div>

        <div className='verification__buttons'>
          <ArrowBtn
            enlarged
            isLoading={isLoading}
            disabled={code.length !== CODE_LENGTH}
            text={t('confirm')}
            action={verifyCode} />
          {!hasRentals && <BackBtn
            text={t('changeNumber')}
            action={() => goToStep('edit')} />}
        </div>
      </div>
    </Popup>
  )

  return (
    <Popup>
      <div className='popup__verification'>
        <div className='popup__title'>
          <div>{t('unverifiedPhoneTitle')}</div>
        </div>

        <p>{t('unverifiedPhoneDescription')}</p>
        {currentPhone && <h2>{currentPhone}</h2>}
        {warning && <p className='verification__warning'>{warning}</p>}

        <div className='verification__buttons'>
          <ArrowBtn
            enlarged
            isLoading={isLoading}
            text={t('sendVerificationCode')}
            action={sendCode} />
          {!hasRentals && <BackBtn
            text={t('changeNumber')}
            action={() => goToStep('edit')} />}
        </div>
      </div>
    </Popup>
  )
}

export default UnverifiedPhonePopup
